import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Retourne le nombre réel d'articles affiché au client.
   * Le supplément de personnalisation à 5 $ n'est pas compté.
   *
   * @param {number} fallbackCount
   * @returns {Promise<number>}
   */
  getVisibleCartCount = async (fallbackCount = 0) => {
    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        return fallbackCount;
      }

      const cart = await response.json();

      if (!Array.isArray(cart.items)) {
        return fallbackCount;
      }

      return cart.items.reduce((count, item) => {
        const properties = item.properties || {};

        if (properties._ea_customization_type === 'custom_text') {
          return count;
        }

        return count + (Number(item.quantity) || 0);
      }, 0);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn(
          '[cart-icon] Could not read customer-facing cart count:',
          error
        );
      }

      return fallbackCount;
    }
  };

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(async ({ cart, detail }) => {
        const fallbackCount =
          cart?.totalQuantity ??
          detail?.itemCount ??
          0;

        const itemCount =
          await this.getVisibleCartCount(fallbackCount);

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-icon] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Renders the cart bubble.
   *
   * @param {number} itemCount
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      itemCount === 0
    );

    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      itemCount === 0
    );

    this.currentCartCount = itemCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) =>
      requestAnimationFrame(resolve)
    );

    this.refs.cartBubble.classList.add(
      'cart-bubble--animating'
    );

    await onAnimationEnd(
      this.refs.cartBubbleText
    );

    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    if (!this.refs.cartBubbleCount) return;

    const currentCount =
      Number.isFinite(this.currentCartCount)
        ? this.currentCartCount
        : 0;

    const visibleCount =
      await this.getVisibleCartCount(currentCount);

    if (visibleCount !== currentCount) {
      await this.renderCartBubble(
        visibleCount,
        false
      );

      return;
    }

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(visibleCount),
        timestamp: Date.now(),
      })
    );
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}